import produce, { current } from 'immer'
import { FACT_ATTRIBUTE_IDS, FACT_IDS } from '../../components/Shared/Factsheet/constants'

const initialState = {
  facts: {},
  validation: {
    facts: {},
    isValid: true
  },
  expandedFactInstances: {},
  blueprint: null,
}

const calculateFactsheetIsValid = (validation) => {
  for (let factDefId in validation.facts) {
    if (validation.facts[factDefId].isValid !== true) {
      return false
    }
  }
  return true
}

function addFactInstance (draft, factDefId, data) {
  if (!draft.facts.hasOwnProperty(factDefId)) {
    draft.facts[factDefId] = []
  }
  if (!draft.expandedFactInstances.hasOwnProperty(factDefId)) {
    draft.expandedFactInstances[factDefId] = Array(
      draft.facts[factDefId].length
    ).fill(false)
  }
  draft.facts[factDefId].push(data)
  draft.expandedFactInstances[factDefId].push(true)
}

function deleteFact (draft, factDefId) {
  if (draft.facts.hasOwnProperty(factDefId)) {
    delete draft.facts[factDefId]
  }
  if (draft.validation.facts.hasOwnProperty(factDefId)) {
    const factIsValid = draft.validation.facts.isValid
    delete draft.validation.facts[factDefId]
    if (!draft.validation.isValid && !factIsValid) {
      draft.validation.isValid = calculateFactsheetIsValid(draft.validation)
    }
  }
  if (draft.expandedFactInstances.hasOwnProperty(factDefId)) {
    delete draft.expandedFactInstances[factDefId]
  }
}

function deleteAttribute (draft, factDefId, factInstanceKey, attributeDefId) {
  if (
    draft.facts.hasOwnProperty(factDefId)
    && draft.facts[factDefId].hasOwnProperty(factInstanceKey)
    && draft.facts[factDefId][factInstanceKey].hasOwnProperty('attributes')
    && draft.facts[factDefId][factInstanceKey].attributes.hasOwnProperty(attributeDefId)
  ) {
    delete draft.facts[factDefId][factInstanceKey].attributes[attributeDefId]
  }
  if (
    draft.validation.facts.hasOwnProperty(factDefId)
    && draft.validation.facts[factDefId].factInstances.hasOwnProperty(factInstanceKey)
    && draft.validation.facts[factDefId].factInstances[factInstanceKey].hasOwnProperty('attributes')
    && draft.validation.facts[factDefId].factInstances[factInstanceKey].attributes.hasOwnProperty(attributeDefId)
  ) {
    delete draft.validation.facts[factDefId].factInstances[factInstanceKey].attributes[attributeDefId]
  }
}

function deleteAttributesByValueType (stateDraft, valueType) {
  if (!stateDraft.blueprint) {
    return
  }
  for (const factDefId in stateDraft.facts) {
    const factInstances = stateDraft.facts[factDefId]
    for (const factInstanceKey in factInstances) {
      const factInstance = factInstances[factInstanceKey]
      if (!factInstance.attributes) {
        continue
      }
      for (const attributeDefId in factInstance.attributes) {
        const attributeValueType = stateDraft.blueprint.factTree.attributes[attributeDefId]?.value?.type
        if (attributeValueType !== valueType) {
          continue
        }
        deleteAttribute(stateDraft, factDefId, factInstanceKey, attributeDefId)
      }
    }
  }
}

const factsheet = produce((draft = initialState, action) => {
  switch (action.type) {
    case 'FACTSHEET_PUT':
      draft.facts = action.payload.factsheet.facts
      draft.validation = { ...initialState.validation }
      draft.expandedFactInstances = {}
      return draft
    case 'FACTSHEET_DELETE_FACT': {
      deleteFact(draft, action.payload.factDefId)
      return draft
    }
    case 'FACTSHEET_ADD_FACT_INSTANCE': {
      addFactInstance(draft, action.payload.factDefId, action.payload.data)
      return draft
    }
    case 'FACTSHEET_PUT_FACT_INSTANCE': {
      if (!draft.facts.hasOwnProperty(action.payload.factDefId)) {
        throw new Error('factDefId not set')
      }
      if (!draft.facts[action.payload.factDefId][action.payload.factInstanceKey]) {
        throw new Error('factInstanceKey not set')
      }
      draft.facts[action.payload.factDefId][action.payload.factInstanceKey] = action.payload.data
      return draft
    }
    case 'FACTSHEET_DELETE_FACT_INSTANCE': {
      if (!draft.facts.hasOwnProperty(action.payload.factDefId)) {
        throw new Error('factDefId not set')
      }
      if (!draft.facts[action.payload.factDefId][action.payload.factInstanceKey]) {
        return draft
      }
      draft.facts[action.payload.factDefId].splice(action.payload.factInstanceKey, 1)
      if (draft.expandedFactInstances.hasOwnProperty(action.payload.factDefId)) {
        draft.expandedFactInstances[action.payload.factDefId].splice(action.payload.factInstanceKey, 1)
      }
      if (
        !draft.validation.facts.hasOwnProperty(action.payload.factDefId)
        || !draft.validation.facts[action.payload.factDefId].factInstances.hasOwnProperty(action.payload.factInstanceKey)
      ) {
        return draft
      }
      delete draft.validation.facts[action.payload.factDefId].factInstances[action.payload.factInstanceKey]
      let factInstances = Object.values(current(draft).validation.facts[action.payload.factDefId].factInstances)
      draft.validation.facts[action.payload.factDefId].factInstances = {}
      for (const factInstanceKey in factInstances) {
        draft.validation.facts[action.payload.factDefId].factInstances[factInstanceKey] = factInstances[factInstanceKey]
      }
      factInstances = current(draft).validation.facts[action.payload.factDefId].factInstances
      let factIsValid = true
      for (const factInstanceKey in factInstances) {
        if (!factInstances[factInstanceKey].isValid) {
          factIsValid = false
          break
        }
      }
      draft.validation.facts[action.payload.factDefId].isValid = factIsValid
      if (!factIsValid) {
        draft.validation.isValid = false
      } else if (factIsValid && !draft.validation.isValid) {
        draft.validation.isValid = calculateFactsheetIsValid(draft.validation)
      }
      return draft
    }
    case 'FACTSHEET_PUT_ATTRIBUTE': {
      if (!draft.facts.hasOwnProperty(action.payload.factDefId)) {
        const factInstanceData = {
          factDefId: action.payload.factDefId,
        }
        addFactInstance(draft, action.payload.factDefId, factInstanceData)
      }
      if (!draft.facts[action.payload.factDefId][action.payload.factInstanceKey].hasOwnProperty('attributes')) {
        draft.facts[action.payload.factDefId][action.payload.factInstanceKey].attributes = {}
      }
      const oldValue = draft.facts[action.payload.factDefId][action.payload.factInstanceKey].attributes[action.payload.attributeDefId]?.value
      draft.facts[action.payload.factDefId][action.payload.factInstanceKey].attributes[action.payload.attributeDefId] = action.payload.data
      if (
        action.payload.factDefId === String(FACT_IDS.COUNTRY)
        && action.payload.attributeDefId === String(FACT_ATTRIBUTE_IDS.COUNTRY_CODE)
      ) {
        if (oldValue && action.payload.data.value !== oldValue) {
          // delete currency fact
          deleteFact(draft, String(FACT_IDS.CURRENCY))
          // delete all pricing attributes
          deleteAttributesByValueType(draft, 'PRICE')
        }
      }
      if (
        action.payload.factDefId === String(FACT_IDS.CURRENCY)
        && action.payload.attributeDefId === String(FACT_ATTRIBUTE_IDS.CURRENCY_CODE)
      ) {
        if (oldValue !== action.payload.data.value) {
          // delete all pricing attributes
          deleteAttributesByValueType(draft, 'PRICE')
        }
      }
      return draft
    }
    case 'FACTSHEET_DELETE_ATTRIBUTE': {
      // delete all pricing attributes if the currency attribute is deleted
      if (action.payload.attributeDefId === String(FACT_ATTRIBUTE_IDS.CURRENCY_CODE)) {
        deleteAttributesByValueType(draft, 'PRICE')
      }
      deleteAttribute(draft, action.payload.factDefId, action.payload.factInstanceKey, action.payload.attributeDefId)
      return draft
    }
    case 'FACTSHEET_PUT_FACT_INSTANCE_EXPANDED_STATUS': {
      if (!draft.expandedFactInstances.hasOwnProperty(action.payload.factDefId)) {
        draft.expandedFactInstances[action.payload.factDefId] = Array(
          draft.facts[action.payload.factDefId].length
        ).fill(false)
      }
      draft.expandedFactInstances[action.payload.factDefId][action.payload.factInstanceKey] = action.payload.expanded
      return draft
    }
    case 'FACTSHEET_PUT_VALIDATION_STATUS': {
      if (!draft.validation.facts.hasOwnProperty(action.payload.factDefId)) {
        draft.validation.facts[action.payload.factDefId] = {
          factInstances: {},
          isValid: true
        }
      }
      if (!draft.validation.facts[action.payload.factDefId].factInstances.hasOwnProperty(action.payload.factInstanceKey)) {
        draft.validation.facts[action.payload.factDefId].factInstances[action.payload.factInstanceKey] = {
          attributes: {},
          isValid: true
        }
      }
      if (!draft.validation.facts[action.payload.factDefId].factInstances[action.payload.factInstanceKey].attributes.hasOwnProperty(action.payload.attributeDefId)) {
        draft.validation.facts[action.payload.factDefId].factInstances[action.payload.factInstanceKey].attributes[action.payload.attributeDefId] = {}
      }
      draft.validation.facts[action.payload.factDefId].factInstances[action.payload.factInstanceKey].attributes[action.payload.attributeDefId].isValid = action.payload.isValid
      const attributes = current(draft).validation.facts[action.payload.factDefId].factInstances[action.payload.factInstanceKey].attributes
      for (const attributeDefId in attributes) {
        if (!attributes[attributeDefId].isValid) {
          draft.validation.facts[action.payload.factDefId].factInstances[action.payload.factInstanceKey].isValid = false
          draft.validation.facts[action.payload.factDefId].isValid = false
          draft.validation.isValid = false
          return draft
        }
      }
      draft.validation.facts[action.payload.factDefId].factInstances[action.payload.factInstanceKey].isValid = true
      const factInstances = current(draft).validation.facts[action.payload.factDefId].factInstances
      for (const factInstanceKey in factInstances) {
        if (!factInstances[factInstanceKey].isValid) {
          draft.validation.facts[action.payload.factDefId].isValid = false
          draft.validation.isValid = false
          return draft
        }
      }
      draft.validation.facts[action.payload.factDefId].isValid = true
      if (draft.validation.isValid !== true) {
        draft.validation.isValid = calculateFactsheetIsValid(draft.validation)
      }
      return draft
    }
    case 'FACTSHEET_PUT_BLUEPRINT': {
      draft.blueprint = action.payload.blueprint
      return draft
    }
    default:
      return draft
  }
})

export default factsheet
